import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  FormControlLabel,
  Switch,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Tooltip,
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  Add,
  Search,
  Delete,
  CloudUpload,
  InsertDriveFile,
  Download,
  Cancel,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useNavigation } from "contexts/notificationsContext";
import { IProjetista, ILinha, ISalvarProjetista } from "shared/interfaces/projetista";
import {
  postProjetista,
  getProjetistaPorId,
  getLinhasSistemas,
  uploadLogoProjetista,
  uploadPacoteLinha,
  removerPacoteLinha,
  downloadPacoteLinha,
  uploadArquivoComum,
  removerArquivoComum,
  downloadArquivoComum,
} from "services/modules/projetistas";
import { getReferenciasArquivoComum } from "services/modules/projetistas";
import { IReferenciaArquivoComum } from "shared/interfaces/projetista";
import {
  getEmpresasComAplicabilidade,
  IEmpresaComAplicabilidade,
} from "services/modules/aplicabilidades";

interface ModalProjetistaProps {
  isOpen: boolean;
  onClose: () => void;
  refreshData: () => void;
  dataProjetista?: IProjetista;
}

interface LinhaVinculada {
  id: string;
  idLinha: number | null;
  projetistaLinhaId?: number;
  arquivoNomeOriginal?: string;
  arquivoTamanhoBytes?: number;
  arquivoDataUpload?: string;
  uploadingPacote?: boolean;
  arquivoPendente?: File;
}

export const ModalProjetista: React.FC<ModalProjetistaProps> = ({
  isOpen,
  onClose,
  refreshData,
  dataProjetista,
}) => {
  const { openNotification } = useNavigation();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const lastLinhaRef = useRef<HTMLDivElement>(null);

  // Dados do formulário
  const [nome, setNome] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [impedirTipologiasDesatualizadas, setImpedirTipologiasDesatualizadas] = useState(false);
  const [ocultarTipologiasSemLiberacao, setOcultarTipologiasSemLiberacao] = useState(false);
  const [superReservado, setSuperReservado] = useState(false);
  const [urlLogo, setUrlLogo] = useState<string>("");

  // Aplicabilidade Utilizada — uma única empresa (null = nenhuma)
  const [idEmpresaAplicabilidade, setIdEmpresaAplicabilidade] = useState<number | null>(null);
  const [empresasAplicabilidade, setEmpresasAplicabilidade] = useState<IEmpresaComAplicabilidade[]>([]);

  // Arquivo Comum (ZIP) — deferred save
  const [arquivoComumPendente, setArquivoComumPendente] = useState<File | null>(null);
  const [arquivoComumNome, setArquivoComumNome] = useState<string | undefined>(undefined);
  const [arquivoComumTamanho, setArquivoComumTamanho] = useState<number | undefined>(undefined);
  const [arquivoComumData, setArquivoComumData] = useState<string | undefined>(undefined);
  const [uploadingArquivoComum, setUploadingArquivoComum] = useState(false);

  // "Usar o arquivo comum na referencia" (decisao P6 de 17/08/2026): o ZIP e enviado uma
  // vez so, aqui, e a referencia escolhida passa a receber o arquivo comum nos pacotes de
  // N1 do CEM Desktop. Uma referencia por projetista — as ja usadas por outro projetista
  // nem aparecem na lista.
  const [arquivoComumIdReferencia, setArquivoComumIdReferencia] = useState<number | null>(null);
  const [referenciasArquivoComum, setReferenciasArquivoComum] = useState<IReferenciaArquivoComum[]>([]);

  const [linhasVinculadas, setLinhasVinculadas] = useState<LinhaVinculada[]>([]);
  const [searchLinha, setSearchLinha] = useState("");
  const [linhasDisponiveis, setLinhasDisponiveis] = useState<ILinha[]>([]);

  // Validação
  const [errors, setErrors] = useState<{
    nome?: string;
    linhas?: string;
  }>({});

  useEffect(() => {
    if (isOpen) {
      // Buscar linhas disponíveis
      const fetchLinhas = async () => {
        const linhas = await getLinhasSistemas();
        setLinhasDisponiveis(linhas);
      };
      fetchLinhas();

      // Buscar empresas com aplicabilidade (combo Aplicabilidade Utilizada)
      const fetchEmpresas = async () => {
        const empresas = await getEmpresasComAplicabilidade();
        setEmpresasAplicabilidade(empresas);
      };
      fetchEmpresas();

      // Referencias para o arquivo comum (P6): a lista ja exclui as de outros projetistas.
      const fetchReferencias = async () => {
        const referencias = await getReferenciasArquivoComum(dataProjetista?.idProjetista);
        setReferenciasArquivoComum(referencias);
      };
      fetchReferencias();

      if (dataProjetista) {
        // Modo edição
        setNome(dataProjetista.nome);
        setAtivo(dataProjetista.ativo);
        setImpedirTipologiasDesatualizadas(dataProjetista.impedirTipologiasDesatualizadas || false);
        setOcultarTipologiasSemLiberacao(dataProjetista.ocultarTipologiasSemLiberacao || false);
        setSuperReservado(dataProjetista.superReservado || false);
        setUrlLogo(dataProjetista.urlLogo || "");
        setIdEmpresaAplicabilidade(dataProjetista.idEmpresaAplicabilidade ?? null);
        setArquivoComumIdReferencia(dataProjetista.arquivoComumIdReferencia ?? null);
        setArquivoComumPendente(null);
        setArquivoComumNome(dataProjetista.arquivoComumNomeOriginal);
        setArquivoComumTamanho(dataProjetista.arquivoComumTamanhoBytes);
        setArquivoComumData(dataProjetista.arquivoComumDataUpload);

        const linhasMap = dataProjetista.linhas.map((linha) => ({
          id: Math.random().toString(36).substr(2, 9),
          idLinha: linha.idLinha,
          projetistaLinhaId: linha.projetistaLinhaId,
          arquivoNomeOriginal: linha.arquivoNomeOriginal,
          arquivoTamanhoBytes: linha.arquivoTamanhoBytes,
          arquivoDataUpload: linha.arquivoDataUpload,
        }));
        setLinhasVinculadas(linhasMap);
      } else {
        // Modo criação
        resetForm();
      }
    }
  }, [isOpen, dataProjetista]);

  const resetForm = () => {
    setNome("");
    setAtivo(true);
    setImpedirTipologiasDesatualizadas(false);
    setOcultarTipologiasSemLiberacao(false);
    setSuperReservado(false);
    setUrlLogo("");
    setIdEmpresaAplicabilidade(null);
    setArquivoComumPendente(null);
    setArquivoComumNome(undefined);
    setArquivoComumTamanho(undefined);
    setArquivoComumData(undefined);
    setLinhasVinculadas([]);
    setSearchLinha("");
    setErrors({});
  };

  const handleAddLinha = () => {
    setLinhasVinculadas([
      ...linhasVinculadas,
      {
        id: Math.random().toString(36).substr(2, 9),
        idLinha: null,
      },
    ]);
    
    // Scroll automático para o novo item após renderização
    setTimeout(() => {
      lastLinhaRef.current?.scrollIntoView({ 
        behavior: "smooth", 
        block: "center" 
      });
    }, 100);
  };

  const handleRemoveLinha = (id: string) => {
    setLinhasVinculadas(linhasVinculadas.filter((linha) => linha.id !== id));
  };

  const handleChangeLinhaVinculada = (id: string, idLinha: number) => {
    setLinhasVinculadas(
      linhasVinculadas.map((linha) =>
        linha.id === id ? { ...linha, idLinha } : linha
      )
    );
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      openNotification({
        message: "Tipo de arquivo não permitido. Use apenas imagens (JPG, PNG, GIF, WEBP)",
        type: "error",
      });
      return;
    }

    // Validar tamanho (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      openNotification({
        message: "Arquivo muito grande. Tamanho máximo: 5MB",
        type: "error",
      });
      return;
    }

    setUploadingLogo(true);
    try {
      const uploadedUrl = await uploadLogoProjetista(file);
      if (uploadedUrl) {
        setUrlLogo(uploadedUrl);
        openNotification({
          message: "Logo enviado com sucesso!",
          type: "success",
        });
      }
    } catch (error: any) {
      openNotification({
        message: error?.message || "Erro ao fazer upload do logo",
        type: "error",
      });
    } finally {
      setUploadingLogo(false);
      // Limpar o input para permitir selecionar o mesmo arquivo novamente
      event.target.value = "";
    }
  };

  const handleUploadPacote = (
    linhaVinculadaId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".xml")) {
      openNotification({
        message: "Apenas arquivos .xml são aceitos.",
        type: "error",
      });
      event.target.value = "";
      return;
    }

    const lv = linhasVinculadas.find((l) => l.id === linhaVinculadaId);
    if (lv?.arquivoNomeOriginal || lv?.arquivoPendente) {
      if (!window.confirm("Já existe um pacote para esta linha. Deseja substituí-lo?")) {
        event.target.value = "";
        return;
      }
    }

    setLinhasVinculadas((prev) =>
      prev.map((l) =>
        l.id === linhaVinculadaId
          ? {
              ...l,
              arquivoPendente: file,
              arquivoNomeOriginal: undefined,
              arquivoTamanhoBytes: undefined,
              arquivoDataUpload: undefined,
            }
          : l
      )
    );

    event.target.value = "";
  };

  const handleRemoverPacote = async (linhaVinculadaId: string) => {
    const lv = linhasVinculadas.find((l) => l.id === linhaVinculadaId);
    if (!lv) return;

    if (lv.arquivoPendente && !lv.arquivoNomeOriginal) {
      setLinhasVinculadas((prev) =>
        prev.map((l) =>
          l.id === linhaVinculadaId ? { ...l, arquivoPendente: undefined } : l
        )
      );
      return;
    }

    if (!lv.projetistaLinhaId) return;

    if (!window.confirm("Tem certeza que deseja remover o pacote desta linha?")) return;

    const success = await removerPacoteLinha(lv.projetistaLinhaId);
    if (success) {
      setLinhasVinculadas((prev) =>
        prev.map((l) =>
          l.id === linhaVinculadaId
            ? {
                ...l,
                arquivoNomeOriginal: undefined,
                arquivoTamanhoBytes: undefined,
                arquivoDataUpload: undefined,
                arquivoPendente: undefined,
              }
            : l
        )
      );
      openNotification({
        message: "Pacote removido com sucesso!",
        type: "success",
      });
    }
  };

  const handleDownloadPacote = async (linhaVinculadaId: string) => {
    const lv = linhasVinculadas.find((l) => l.id === linhaVinculadaId);
    if (!lv?.projetistaLinhaId) return;
    await downloadPacoteLinha(lv.projetistaLinhaId);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const temArquivoComum = !!arquivoComumPendente || !!arquivoComumNome;

  const handleSelecionarArquivoComum = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // VF1: extensão .zip
    if (!file.name.toLowerCase().endsWith(".zip")) {
      openNotification({
        message: "Apenas arquivos .zip são aceitos.",
        type: "error",
      });
      event.target.value = "";
      return;
    }

    // VF2: tamanho máximo 100 MB
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      openNotification({
        message: "Arquivo muito grande. Tamanho máximo: 100MB",
        type: "error",
      });
      event.target.value = "";
      return;
    }

    // VF3: confirmação ao substituir
    if (temArquivoComum) {
      if (!window.confirm("Já existe um Arquivo Comum. Deseja substituí-lo?")) {
        event.target.value = "";
        return;
      }
    }

    setArquivoComumPendente(file);
    setArquivoComumNome(undefined);
    setArquivoComumTamanho(undefined);
    setArquivoComumData(undefined);
    event.target.value = "";
  };

  const handleRemoverArquivoComum = async () => {
    // Arquivo apenas selecionado (ainda não enviado): limpa o pendente.
    if (arquivoComumPendente && !arquivoComumNome) {
      setArquivoComumPendente(null);
      return;
    }

    // VF4: confirmação com aviso de impacto no CEM (RN7)
    if (
      !window.confirm(
        "Tem certeza que deseja remover o Arquivo Comum?\n\n" +
          "O CEM deixará de receber o Arquivo Comum nas referências deste projetista."
      )
    ) {
      return;
    }

    // Só há o que remover no servidor se o projetista já existe.
    if (dataProjetista?.idProjetista) {
      const success = await removerArquivoComum(dataProjetista.idProjetista);
      if (!success) return;
    }

    setArquivoComumPendente(null);
    setArquivoComumNome(undefined);
    setArquivoComumTamanho(undefined);
    setArquivoComumData(undefined);
    openNotification({
      message: "Arquivo Comum removido com sucesso!",
      type: "success",
    });
  };

  const handleDownloadArquivoComum = async () => {
    if (!dataProjetista?.idProjetista) return;
    await downloadArquivoComum(dataProjetista.idProjetista);
  };

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!nome.trim()) {
      newErrors.nome = "O Nome é obrigatório";
    }

    const linhasSelecionadas = linhasVinculadas.filter((l) => l.idLinha);
    
    if (linhasSelecionadas.length === 0) {
      newErrors.linhas = "É necessário associar pelo menos uma Linha/Sistema";
    } else {
      // Verificar se há linhas duplicadas
      const linhaIds = linhasSelecionadas.map((l) => l.idLinha);
      const linhasUnicas = new Set(linhaIds);
      if (linhaIds.length !== linhasUnicas.size) {
        newErrors.linhas = "Não é permitido associar a mesma Linha/Sistema mais de uma vez";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      openNotification({
        message: "Por favor, preencha todos os campos obrigatórios",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const payload: ISalvarProjetista = {
        idProjetista: dataProjetista?.idProjetista || 0,
        nome,
        ativo,
        impedirTipologiasDesatualizadas,
        ocultarTipologiasSemLiberacao,
        superReservado,
        urlLogo: urlLogo || undefined,
        idEmpresaAplicabilidade: idEmpresaAplicabilidade ?? undefined,
        arquivoComumIdReferencia: arquivoComumIdReferencia ?? undefined,
        linhaIds: linhasVinculadas.filter(l => l.idLinha).map(l => l.idLinha!),
      };
      
      const projetistaId = await postProjetista(payload);

      if (!projetistaId) return;

      let algumPacoteFalhou = false;
      const pendentes = linhasVinculadas.filter(l => l.arquivoPendente && l.idLinha);
      if (pendentes.length > 0) {
        const projetistaSalvo = await getProjetistaPorId(projetistaId);
        if (projetistaSalvo) {
          for (const lv of pendentes) {
            const linhaSalva = projetistaSalvo.linhas.find(l => l.idLinha === lv.idLinha);
            if (linhaSalva?.projetistaLinhaId && lv.arquivoPendente) {
              setLinhasVinculadas((prev) =>
                prev.map((l) =>
                  l.id === lv.id ? { ...l, uploadingPacote: true } : l
                )
              );
              try {
                // uploadPacoteLinha retorna null em caso de falha (não lança).
                const upload = await uploadPacoteLinha(lv.arquivoPendente, linhaSalva.projetistaLinhaId);
                if (!upload) throw new Error("upload-falhou");
              } catch {
                algumPacoteFalhou = true;
                openNotification({
                  message: `Falha ao enviar pacote da linha ${linhaSalva.idLinha}. Você pode reenviá-lo editando o projetista.`,
                  type: "warning",
                });
              }
            }
          }
        }
      }

      // Arquivo Comum (ZIP) pendente — enviar após o projetista existir (id retornado)
      if (arquivoComumPendente) {
        setUploadingArquivoComum(true);
        try {
          await uploadArquivoComum(arquivoComumPendente, projetistaId);
        } catch {
          openNotification({
            message:
              "Falha ao enviar o Arquivo Comum. Você pode reenviá-lo editando o projetista.",
            type: "warning",
          });
        } finally {
          setUploadingArquivoComum(false);
        }
      }

      // Não anunciar sucesso se algum pacote de tipologia falhou no envio — o
      // aviso por linha já foi exibido e o usuário pode reenviar editando.
      if (!algumPacoteFalhou) {
        openNotification({
          message: `Projetista ${dataProjetista ? "atualizado" : "cadastrado"} com sucesso!`,
          type: "success",
        });
      }

      handleClose();
      refreshData();
    } catch (error: any) {
      openNotification({
        message: error?.message || "Erro ao salvar projetista",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getLinhasFiltradas = () => {
    let linhas = linhasDisponiveis;
    
    if (searchLinha.trim()) {
      linhas = linhas.filter((linha) =>
        linha.nome.toLowerCase().includes(searchLinha.toLowerCase())
      );
    }
    
    return linhas;
  };

  // Verifica se uma linha já está selecionada em outro item
  const isLinhaJaSelecionada = (linhaId: number, currentItemId: string) => {
    return linhasVinculadas.some(
      (l) => l.idLinha === linhaId && l.id !== currentItemId
    );
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            Cadastro de projetista
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} mt={1}>
          {/* Nome do Projetista */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome do projetista"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              error={!!errors.nome}
              helperText={errors.nome || "Obrigatório"}
              required
            />
          </Grid>

          {/* Aplicabilidade Utilizada — empresa cujas aplicabilidades vão no pacote de materiais */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Aplicabilidade Utilizada"
              value={idEmpresaAplicabilidade ?? ""}
              onChange={(e) =>
                setIdEmpresaAplicabilidade(e.target.value === "" ? null : Number(e.target.value))
              }
              helperText="Empresa cujas aplicabilidades serão consultadas ao gerar o pacote de materiais das tipologias"
            >
              <MenuItem value="">
                <em>Nenhuma</em>
              </MenuItem>
              {empresasAplicabilidade.map((empresa) => (
                <MenuItem key={empresa.idEmpresa} value={empresa.idEmpresa}>
                  {empresa.nomeFantasia}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Upload de Logo */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{ 
                    minWidth: 100,
                    maxWidth: 160,
                    height: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: urlLogo ? "transparent" : "grey.100",
                    border: "2px solid",
                    borderColor: urlLogo ? "primary.main" : "grey.400",
                    borderStyle: urlLogo ? "solid" : "dashed",
                    borderRadius: 1,
                    overflow: "hidden",
                    p: urlLogo ? 0.5 : 0
                  }}
                >
                  {urlLogo ? (
                    <img
                      src={urlLogo}
                      alt="Logo do Projetista"
                      style={{ 
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain"
                      }}
                    />
                  ) : (
                    <Box sx={{ fontSize: "2rem", color: "grey.500" }}>L</Box>
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="logo-upload-input"
                    type="file"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                  />
                  <label htmlFor="logo-upload-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUpload />}
                      disabled={uploadingLogo}
                      fullWidth
                    >
                      {uploadingLogo ? "Enviando..." : urlLogo ? "Alterar Logo" : "Enviar Logo"}
                    </Button>
                  </label>
                  <Box sx={{ mt: 1, fontSize: "0.75rem", color: "text.secondary" }}>
                    Formatos aceitos: JPG, PNG, GIF, WEBP (máx. 5MB)
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Arquivo Comum (ZIP) */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Arquivo Comum (ZIP)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Propagado para as referências das linhas vinculadas — o CEM o recebe nos pacotes com "Incluir arquivo comum".
            </Typography>

            {uploadingArquivoComum ? (
              <Box sx={{
                border: "1px solid",
                borderColor: "primary.main",
                borderRadius: 1,
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Enviando Arquivo Comum...</Typography>
              </Box>
            ) : arquivoComumPendente ? (
              <Box sx={{
                border: "1px solid",
                borderColor: "warning.main",
                borderRadius: 1,
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "warning.50",
              }}>
                <InsertDriveFile color="warning" fontSize="small" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {arquivoComumPendente.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(arquivoComumPendente.size)} — será enviado ao salvar
                  </Typography>
                </Box>
                <Tooltip title="Remover Arquivo Comum">
                  <IconButton size="small" color="error" onClick={handleRemoverArquivoComum}>
                    <Cancel fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : arquivoComumNome ? (
              <Box sx={{
                border: "1px solid",
                borderColor: "primary.main",
                borderRadius: 1,
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}>
                <InsertDriveFile color="primary" fontSize="small" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {arquivoComumNome}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {arquivoComumTamanho ? formatBytes(arquivoComumTamanho) : ""}
                    {arquivoComumData
                      ? ` — enviado em ${new Date(arquivoComumData).toLocaleDateString("pt-BR")}`
                      : ""}
                  </Typography>
                </Box>
                <Tooltip title="Baixar Arquivo Comum">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleDownloadArquivoComum}
                      disabled={!dataProjetista?.idProjetista}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remover Arquivo Comum">
                  <IconButton size="small" color="error" onClick={handleRemoverArquivoComum}>
                    <Cancel fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box>
                <input
                  accept=".zip"
                  style={{ display: "none" }}
                  id="arquivo-comum-upload-input"
                  type="file"
                  onChange={handleSelecionarArquivoComum}
                />
                <label htmlFor="arquivo-comum-upload-input">
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      border: "2px dashed",
                      borderColor: "grey.400",
                      borderRadius: 1,
                      p: 1.5,
                      cursor: "pointer",
                      "&:hover": { borderColor: "primary.main", bgcolor: "grey.50" },
                    }}
                  >
                    <CloudUpload fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Clique para fazer upload do ZIP (máx. 100MB)
                    </Typography>
                  </Box>
                </label>
              </Box>
            )}

            {/* Decisao P6 (17/08/2026): a referencia que TAMBEM usa este arquivo comum. */}
            <TextField
              fullWidth
              select
              sx={{ mt: 2 }}
              label="Usar o arquivo comum na referência"
              value={arquivoComumIdReferencia ?? ""}
              onChange={(e) =>
                setArquivoComumIdReferencia(e.target.value === "" ? null : Number(e.target.value))
              }
              helperText="A referência escolhida recebe este arquivo comum nos pacotes de cadastros N1 do CEM Desktop"
            >
              <MenuItem value="">
                <em>Nenhuma</em>
              </MenuItem>
              {referenciasArquivoComum.map((referencia) => (
                <MenuItem key={referencia.idReferencia} value={referencia.idReferencia}>
                  {referencia.descricao}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Switches */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                  color="primary"
                />
              }
              label="Ativo"
            />
          </Grid>

          <Grid item xs={12}>
            <Tooltip title="O software CEM Desktop impede que as tipologias desatualizadas deste projetista sejam utilizadas pelo usuário até que sejam atualizadas.">
              <FormControlLabel
                control={
                  <Switch
                    checked={impedirTipologiasDesatualizadas}
                    onChange={(e) => setImpedirTipologiasDesatualizadas(e.target.checked)}
                    color="primary"
                  />
                }
                label="Impedir uso de tipologias desatualizadas - CEM PRO"
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <Tooltip title="O software CEM Desktop oculta as tipologias do Projetista + Linha/Sistema que existir no cadastro e que não estejam mais disponíveis para uso.">
              <FormControlLabel
                control={
                  <Switch
                    checked={ocultarTipologiasSemLiberacao}
                    onChange={(e) => setOcultarTipologiasSemLiberacao(e.target.checked)}
                    color="primary"
                  />
                }
                label="Ocultar tipologias sem liberação para uso - CEM PRO"
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={superReservado}
                  onChange={(e) => setSuperReservado(e.target.checked)}
                  color="primary"
                />
              }
              label="Projetista de uso super reservado"
            />
          </Grid>

          {/* Linhas Vinculadas */}
          <Grid item xs={12} mt={2}>
            <Grid container justifyContent="space-between" alignItems="center" mb={2}>
              <Grid item>
                <strong>Linhas vinculadas ao projetista</strong>
              </Grid>
            </Grid>

            {/* Campo de Busca */}
            <TextField
              fullWidth
              placeholder="Faça uma busca"
              value={searchLinha}
              onChange={(e) => setSearchLinha(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Botão Adicionar */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddLinha}
              fullWidth
              sx={{ mb: 2 }}
            >
              ADICIONAR
            </Button>

            {/* Lista de Linhas Vinculadas */}
            {linhasVinculadas.length === 0 ? (
              <Grid container justifyContent="center" py={2}>
                <Grid item>
                  {errors.linhas && (
                    <span style={{ color: "red", fontSize: "14px" }}>
                      {errors.linhas}
                    </span>
                  )}
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                {linhasVinculadas.map((linhaVinculada, index) => (
                  <Grid 
                    item 
                    xs={12} 
                    key={linhaVinculada.id}
                    ref={index === linhasVinculadas.length - 1 ? lastLinhaRef : null}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={1}>
                        <strong>{index + 1}.</strong>
                      </Grid>
                      <Grid item xs={10}>
                        <TextField
                          fullWidth
                          select
                          label="Linha/Sistema"
                          value={linhaVinculada.idLinha || ""}
                          onChange={(e) =>
                            handleChangeLinhaVinculada(
                              linhaVinculada.id,
                              Number(e.target.value)
                            )
                          }
                          required
                          helperText="Obrigatório"
                        >
                          <MenuItem value="">
                            <em>Selecione...</em>
                          </MenuItem>
                          {getLinhasFiltradas().map((linha) => {
                            const jaSelecionada = isLinhaJaSelecionada(linha.idLinha, linhaVinculada.id);
                            return (
                              <MenuItem 
                                key={linha.idLinha} 
                                value={linha.idLinha}
                                disabled={jaSelecionada}
                                sx={jaSelecionada ? { opacity: 0.5 } : {}}
                              >
                                {linha.nome}
                                {jaSelecionada && " (já selecionada)"}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveLinha(linhaVinculada.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>

                    {/* Área de Upload de Pacote ZIP por Linha */}
                    <Box sx={{ ml: "8.33%", mt: 1, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                        Arquivo XML de tipologias (método legado CEM)
                      </Typography>

                      {linhaVinculada.uploadingPacote ? (
                        <Box sx={{
                          border: "1px solid",
                          borderColor: "primary.main",
                          borderRadius: 1,
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">
                            Processando pacote...
                          </Typography>
                        </Box>
                      ) : linhaVinculada.arquivoPendente ? (
                        <Box sx={{
                          border: "1px solid",
                          borderColor: "warning.main",
                          borderRadius: 1,
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          bgcolor: "warning.50",
                        }}>
                          <InsertDriveFile color="warning" fontSize="small" />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {linhaVinculada.arquivoPendente.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatBytes(linhaVinculada.arquivoPendente.size)} — será enviado ao salvar
                            </Typography>
                          </Box>
                          <Tooltip title="Remover pacote">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoverPacote(linhaVinculada.id)}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <input
                              accept=".xml"
                              style={{ display: "none" }}
                              id={`pacote-upload-${linhaVinculada.id}`}
                              type="file"
                              onChange={(e) => handleUploadPacote(linhaVinculada.id, e)}
                              disabled={false}
                            />
                            <label htmlFor={`pacote-upload-${linhaVinculada.id}`}>
                              <Box
                                component="span"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1,
                                  border: "2px dashed",
                                  borderColor: "grey.400",
                                  borderRadius: 1,
                                  p: 1.5,
                                  cursor: "pointer",
                                  opacity: 1,
                                  "&:hover": { borderColor: "primary.main", bgcolor: "grey.50" },
                                }}
                              >
                                <CloudUpload
                                  fontSize="small"
                                  color="action"
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Clique para fazer upload do XML de tipologias
                                </Typography>
                              </Box>
                            </label>
                          </Box>
                          <Tooltip title="Baixar pacote">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!linhaVinculada.arquivoNomeOriginal}
                                onClick={() => handleDownloadPacote(linhaVinculada.id)}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Remover pacote">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={!linhaVinculada.arquivoNomeOriginal}
                                onClick={() => handleRemoverPacote(linhaVinculada.id)}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          {/* Botões de Ação */}
          <Grid item xs={12} mt={3}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={loading}
                >
                  CANCELAR
                </Button>
              </Grid>
              <Grid item>
                <LoadingButton
                  variant="contained"
                  onClick={handleSave}
                  loading={loading}
                >
                  SALVAR
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

